import {
    createSlice,
    createEntityAdapter,
    createAsyncThunk,
    PayloadAction,
} from "@reduxjs/toolkit";
import type {
    Task,
    TaskStatus,
    TaskViewType,
    TaskFilters,
} from "../../pages/TaskAndGoals/types/task.types";
import { LEGACY_STATUS_MAP } from "../../pages/TaskAndGoals/constants/task.constants";
import { taskApi } from "../../services/apis/task.api";
import type { RootState } from "../store";

// Normalize legacy status values to new ones
function normalizeStatus(status: string): TaskStatus {
    if (status in LEGACY_STATUS_MAP) {
        return LEGACY_STATUS_MAP[status as keyof typeof LEGACY_STATUS_MAP];
    }
    return status as TaskStatus;
}

function normalizeTask(raw: any): Task {
    return {
        ...raw,
        status: normalizeStatus(raw.status || "todo"),
        priority: raw.priority || "none",
        labels: raw.labels || [],
        comments: raw.comments || [],
        activityLog: raw.activityLog || [],
        sortOrder: raw.sortOrder || 0,
        parentTask: raw.parentTask || null,
    };
}

const taskAdapter = createEntityAdapter<Task>({
    selectId: (task) => task._id,
    sortComparer: (a, b) => a.sortOrder - b.sortOrder || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

// --- Async Thunks ---

export const fetchTasks = createAsyncThunk(
    "tasks/fetchTasks",
    async (
        { orgId, header, filters }: { orgId: string; header: any; filters?: TaskFilters },
        { rejectWithValue }
    ) => {
        try {
            const res = await taskApi.getAll(orgId, header, filters);
            return (res.data || []).map(normalizeTask);
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to fetch tasks");
        }
    }
);

export const fetchUserTasks = createAsyncThunk(
    "tasks/fetchUserTasks",
    async ({ userId, header }: { userId: string; header: any }, { rejectWithValue }) => {
        try {
            const res = await taskApi.getByUser(userId, header);
            return (res.data || []).map(normalizeTask);
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to fetch user tasks");
        }
    }
);

export const fetchBoardTasks = createAsyncThunk(
    "tasks/fetchBoardTasks",
    async ({ orgId, header }: { orgId: string; header: any }, { rejectWithValue }) => {
        try {
            const res = await taskApi.getGroupedByStatus(orgId, header);
            const grouped = res.data || {};
            const allTasks: Task[] = [];
            for (const status of Object.keys(grouped)) {
                for (const task of grouped[status]) {
                    allTasks.push(normalizeTask(task));
                }
            }
            return allTasks;
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to fetch board tasks");
        }
    }
);

export const createTask = createAsyncThunk(
    "tasks/createTask",
    async ({ body, header }: { body: Record<string, any>; header: any }, { rejectWithValue }) => {
        try {
            const res = await taskApi.create(body, header);
            const tasks = res.data;
            return Array.isArray(tasks) ? tasks.map(normalizeTask) : [normalizeTask(tasks)];
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to create task");
        }
    }
);

export const updateTaskField = createAsyncThunk(
    "tasks/updateTaskField",
    async (
        { taskId, field, value, header }: { taskId: string; field: string; value: any; header: any },
        { rejectWithValue }
    ) => {
        try {
            const res = await taskApi.updateField(taskId, field, value, header);
            return normalizeTask(res.data);
        } catch (err: any) {
            return rejectWithValue({ taskId, error: err.message || "Failed to update" });
        }
    }
);

export const bulkUpdateTasks = createAsyncThunk(
    "tasks/bulkUpdateTasks",
    async (
        { taskIds, updates, header }: { taskIds: string[]; updates: Record<string, any>; header: any },
        { rejectWithValue }
    ) => {
        try {
            await taskApi.bulkUpdate(taskIds, updates, header);
            return { taskIds, updates };
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to bulk update");
        }
    }
);

export const deleteTask = createAsyncThunk(
    "tasks/deleteTask",
    async ({ taskId, header }: { taskId: string; header: any }, { rejectWithValue }) => {
        try {
            await taskApi.delete(taskId, header);
            return taskId;
        } catch (err: any) {
            return rejectWithValue({ taskId, error: err.message || "Failed to delete" });
        }
    }
);

export const addComment = createAsyncThunk(
    "tasks/addComment",
    async (
        { taskId, text, header }: { taskId: string; text: string; header: any },
        { rejectWithValue }
    ) => {
        try {
            const res = await taskApi.addComment(taskId, text, header);
            const comments = Array.isArray(res.data) ? res.data : (res.data?.comments ?? []);
            return { taskId, comments };
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to add comment");
        }
    }
);

// --- Slice ---

interface OptimisticEntry {
    original: Partial<Task>;
    timestamp: number;
}

interface TaskState {
    activeView: TaskViewType;
    filters: TaskFilters;
    selectedTaskIds: string[];
    detailPanelTaskId: string | null;
    loading: boolean;
    error: string | null;
    optimisticUpdates: Record<string, OptimisticEntry>;
}

const initialExtraState: TaskState = {
    activeView: "list",
    filters: {},
    selectedTaskIds: [],
    detailPanelTaskId: null,
    loading: false,
    error: null,
    optimisticUpdates: {},
};

const taskSlice = createSlice({
    name: "tasks",
    initialState: taskAdapter.getInitialState(initialExtraState),
    reducers: {
        setActiveView(state, action: PayloadAction<TaskViewType>) {
            state.activeView = action.payload;
        },
        setFilters(state, action: PayloadAction<Partial<TaskFilters>>) {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters(state) {
            state.filters = {};
        },
        setSelectedTaskIds(state, action: PayloadAction<string[]>) {
            state.selectedTaskIds = action.payload;
        },
        toggleTaskSelection(state, action: PayloadAction<string>) {
            const id = action.payload;
            const idx = state.selectedTaskIds.indexOf(id);
            if (idx >= 0) {
                state.selectedTaskIds.splice(idx, 1);
            } else {
                state.selectedTaskIds.push(id);
            }
        },
        clearSelection(state) {
            state.selectedTaskIds = [];
        },
        openDetailPanel(state, action: PayloadAction<string>) {
            state.detailPanelTaskId = action.payload;
        },
        closeDetailPanel(state) {
            state.detailPanelTaskId = null;
        },
        // Optimistic update - immediately apply, store original for rollback
        taskUpdatedOptimistically(
            state,
            action: PayloadAction<{ taskId: string; changes: Partial<Task> }>
        ) {
            const { taskId, changes } = action.payload;
            const existing = state.entities[taskId];
            if (existing) {
                // Store original values for fields being changed
                const original: Partial<Task> = {};
                for (const key of Object.keys(changes)) {
                    (original as any)[key] = (existing as any)[key];
                }
                state.optimisticUpdates[taskId] = {
                    original,
                    timestamp: Date.now(),
                };
                taskAdapter.updateOne(state, { id: taskId, changes });
            }
        },
        // Revert an optimistic update on API failure
        taskUpdateReverted(state, action: PayloadAction<string>) {
            const taskId = action.payload;
            const entry = state.optimisticUpdates[taskId];
            if (entry) {
                taskAdapter.updateOne(state, { id: taskId, changes: entry.original });
                delete state.optimisticUpdates[taskId];
            }
        },
        // SSE: another user changed a task - apply if not our own recent optimistic update
        taskReceivedFromServer(state, action: PayloadAction<Task>) {
            const task = action.payload;
            const entry = state.optimisticUpdates[task._id];
            const WINDOW = 5000;
            if (entry && Date.now() - entry.timestamp < WINDOW) {
                // Skip - our own optimistic update is still fresh
                return;
            }
            delete state.optimisticUpdates[task._id];
            taskAdapter.upsertOne(state, task);
        },
        taskAddedFromServer(state, action: PayloadAction<Task>) {
            taskAdapter.upsertOne(state, action.payload);
        },
        taskRemovedFromServer(state, action: PayloadAction<string>) {
            taskAdapter.removeOne(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        // fetchTasks
        builder.addCase(fetchTasks.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTasks.fulfilled, (state, action) => {
            state.loading = false;
            taskAdapter.setAll(state, action.payload);
        });
        builder.addCase(fetchTasks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // fetchUserTasks
        builder.addCase(fetchUserTasks.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchUserTasks.fulfilled, (state, action) => {
            state.loading = false;
            taskAdapter.setAll(state, action.payload);
        });
        builder.addCase(fetchUserTasks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // fetchBoardTasks
        builder.addCase(fetchBoardTasks.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchBoardTasks.fulfilled, (state, action) => {
            state.loading = false;
            taskAdapter.setAll(state, action.payload);
        });
        builder.addCase(fetchBoardTasks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // createTask
        builder.addCase(createTask.fulfilled, (state, action) => {
            taskAdapter.upsertMany(state, action.payload);
        });

        // updateTaskField (server confirmation after optimistic update)
        builder.addCase(updateTaskField.fulfilled, (state, action) => {
            const task = action.payload;
            delete state.optimisticUpdates[task._id];
            taskAdapter.upsertOne(state, task);
        });
        builder.addCase(updateTaskField.rejected, (state, action) => {
            const payload = action.payload as { taskId: string; error: string } | undefined;
            if (payload?.taskId) {
                const entry = state.optimisticUpdates[payload.taskId];
                if (entry) {
                    taskAdapter.updateOne(state, {
                        id: payload.taskId,
                        changes: entry.original,
                    });
                    delete state.optimisticUpdates[payload.taskId];
                }
            }
        });

        // bulkUpdateTasks
        builder.addCase(bulkUpdateTasks.fulfilled, (state, action) => {
            const { taskIds, updates } = action.payload;
            const normalized: Partial<Task> = {};
            if (updates.status) normalized.status = normalizeStatus(updates.status);
            if (updates.priority) normalized.priority = updates.priority;
            if (updates.assignedTo) normalized.assignedTo = updates.assignedTo;
            taskAdapter.updateMany(
                state,
                taskIds.map((id) => ({ id, changes: { ...normalized } }))
            );
            state.selectedTaskIds = [];
        });

        // deleteTask
        builder.addCase(deleteTask.fulfilled, (state, action) => {
            taskAdapter.removeOne(state, action.payload);
            state.selectedTaskIds = state.selectedTaskIds.filter(
                (id) => id !== action.payload
            );
            if (state.detailPanelTaskId === action.payload) {
                state.detailPanelTaskId = null;
            }
        });
        builder.addCase(deleteTask.rejected, (_state, _action) => {
            // Task could not be deleted - caller should refetch if needed
        });

        // addComment
        builder.addCase(addComment.fulfilled, (state, action) => {
            const { taskId, comments } = action.payload;
            taskAdapter.updateOne(state, {
                id: taskId,
                changes: { comments },
            });
        });
    },
});

export const {
    setActiveView,
    setFilters,
    clearFilters,
    setSelectedTaskIds,
    toggleTaskSelection,
    clearSelection,
    openDetailPanel,
    closeDetailPanel,
    taskUpdatedOptimistically,
    taskUpdateReverted,
    taskReceivedFromServer,
    taskAddedFromServer,
    taskRemovedFromServer,
} = taskSlice.actions;

export const taskReducer = taskSlice.reducer;

// --- Selectors ---
export const {
    selectAll: selectAllTasks,
    selectById: selectTaskById,
    selectIds: selectTaskIds,
    selectEntities: selectTaskEntities,
    selectTotal: selectTaskTotal,
} = taskAdapter.getSelectors((state: RootState) => state.tasks);
